import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useFieldArray } from 'react-hook-form'
// @ts-ignore
import { Button, Autocomplete, Tooltip, Trash, IconButton, QLogo } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { useDisplayTheme } from 'hooks/useUiState'
import { AutomationId } from 'utils/automationUtil'
import { usePageConfigUser } from 'hooks/usePageConfig'
import {
  useLiveReportInfo,
  useLiveReportSavingInfo,
  useLiveReportSuggestedUserList
} from 'hooks/useLiveReportConfigModalState'
import { SuggestedUserInfo } from 'features/suggestedUsers/types'
import ErrorCode from 'enums/ErrorCode'
import {
  StyledLogo,
  StyledActionsContainer,
  StyledSubscribersArea,
  StyledSubscribersListContainer,
  StyledLoadingList,
  StyledSubscribersListItem,
  StyledSubscribersEmail,
  StyledSubscribersHint,
  StyledLoadingField
} from './index.css'
import { SubscriberUserInfo } from './types'
import { handleLiveReportSubscriber } from './utils'
import useLiveReportUserList from './hooks/useLiveReportUserList'

const MAX_SUBSCRIBER_COUNT = 20
const EMAIL_VALIDATION = /^[+a-zA-Z0-9_.'&-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z0-9]{2,12}$/

const SubscriberEmailItem = ({ formattedSubscriber }: { formattedSubscriber: string }) => {
  const theme = useDisplayTheme()
  const [displaySubscribersTooltip, setDisplaySubscribersTooltip] = useState(false)
  const subscribersContentRef = useRef<HTMLDivElement>(null)

  const checkSubscriberContent = useCallback(() => {
    if (!subscribersContentRef.current) return
    const subscribersContent = subscribersContentRef.current
    setDisplaySubscribersTooltip(subscribersContent.scrollWidth > subscribersContent.clientWidth)
  }, [])

  useEffect(() => {
    checkSubscriberContent()
  })
  return (
    <StyledSubscribersEmail ref={subscribersContentRef as React.MutableRefObject<HTMLDivElement>}>
      <Tooltip mode={theme} enabled={displaySubscribersTooltip} content={formattedSubscriber}>
        <span>{formattedSubscriber}</span>
      </Tooltip>
    </StyledSubscribersEmail>
  )
}

const Subscribers = () => {
  const theme = useDisplayTheme()
  const { t } = useTranslation()

  const [selectedUser, setSelectedUser] = useState<SubscriberUserInfo | null>(null)
  const subscribersAutocompleteRef = useRef<HTMLFormElement>(null)

  const savingInfo = useLiveReportSavingInfo()
  const liveReportInfo = useLiveReportInfo()
  const liveReportSuggestedUserList = useLiveReportSuggestedUserList()
  const userConfig = usePageConfigUser()

  const { register, setValue, control, getValues } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'subscribersList',
    keyName: 'key'
  })

  // initialize subscribersList
  useEffect(() => {
    const isV3Enabled = !liveReportInfo.reportEnabled && liveReportInfo.subscriberInfos.length > 0
    const isV4Enabled = liveReportInfo.reportEnabled

    if (liveReportInfo.status === 'succeeded' && userConfig) {
      setValue(
        'subscribersList',
        isV3Enabled || isV4Enabled
          ? liveReportInfo.subscriberInfos
          : [{ id: userConfig.userProfileId }]
      )
    }
  }, [liveReportInfo, userConfig, setValue, getValues])

  const subscribersIdList = getValues('subscribersList')?.map((item: { id: string }) => item.id)

  const { suggestedUserList, suggestedUserLookup, annotatorList } = useLiveReportUserList(
    subscribersIdList
  )

  const isAutoCompleteDisabled = getValues('subscribersList')?.length >= MAX_SUBSCRIBER_COUNT

  useEffect(() => {
    const savingInfoSet = new Set(savingInfo.failedSubscribers)

    if (savingInfo.statusCode === ErrorCode.ADMIN_NO_EXECUTIVE_USER_QUOTA) {
      const targetEmail = savingInfo.failedSubscribers[0]
      const idToRemove = Array.from(suggestedUserLookup.entries()).find(
        ([, userInfo]) => userInfo.email === targetEmail
      )?.[0]

      setValue(
        'subscribersList',
        getValues('subscribersList').filter(
          (subscriber: { id: string; name?: string }) =>
            subscriber.id !== idToRemove && subscriber?.name !== targetEmail
        )
      )
    }

    if (savingInfo.statusCode === ErrorCode.DASHBOARD_CAN_NOT_ADD_LIVEREPORT_SUBSCRIBER) {
      setValue(
        'subscribersList',
        getValues('subscribersList').filter((subscriber: { id: string; name?: string }) => {
          const userInfo = suggestedUserLookup.get(subscriber.id)
          if (userInfo?.email) {
            return !savingInfoSet.has(userInfo.email!)
          }
          return !savingInfoSet.has(subscriber.name!)

          return null
        })
      )
    }
  }, [savingInfo, getValues, setValue, suggestedUserLookup])

  const handleSelect = (targetValue: SubscriberUserInfo) => {
    setSelectedUser(targetValue)
  }

  const handleAddSubscriber = () => {
    if (!selectedUser && subscribersAutocompleteRef?.current?.value) {
      setSelectedUser(null)
      const inputEmail = subscribersAutocompleteRef.current.value

      const isValidEmail = EMAIL_VALIDATION.test(inputEmail)
      const isNotAnnotator = !annotatorList.includes(inputEmail)
      const isNotDuplicate =
        !subscribersIdList.some(
          (id: string) => suggestedUserLookup.get(id)?.email === inputEmail
        ) &&
        !getValues('subscribersList').some(
          (subscriber: { name?: string; id: string }) => subscriber.name === inputEmail
        )

      const existingUser = Array.from(suggestedUserLookup.entries()).find(
        ([, userInfo]) => userInfo.email === inputEmail
      )
      const isExistSuggestedUser = !!existingUser

      if (isValidEmail && isNotDuplicate && isNotAnnotator) {
        if (!isExistSuggestedUser) {
          append({
            id: '-1',
            name: inputEmail
          })
        } else {
          const existingUserId = existingUser?.[0]
          const target = suggestedUserLookup.get(existingUserId)
          if (target) {
            append({
              id: target.userProfileId
            })
          }
        }
      }
    }
    if (selectedUser?.id) {
      const target = suggestedUserLookup.get(selectedUser.id)
      if (target) {
        append({
          id: target.userProfileId
        })
      }
    }

    if (subscribersAutocompleteRef.current) {
      subscribersAutocompleteRef.current.reset()
      setSelectedUser(null)
    }
  }

  const handleRemoveSubscriber = (idx: number) => {
    remove(idx)
  }

  const formatSubscriberDisplay = (
    subscriber: SuggestedUserInfo | { userProfileId: string; userName: string }
  ) => {
    if ('email' in subscriber) {
      return `${subscriber?.userName} (${subscriber.email})`
    }
    return subscriber?.userName
  }

  return (
    <StyledSubscribersArea data-testid={AutomationId.liveReportModal.subscribersContainer}>
      <Tooltip
        mode={theme}
        enabled={isAutoCompleteDisabled}
        content={t('subscriberAutoCompleteDisableTooltip', { amount: MAX_SUBSCRIBER_COUNT })}>
        <StyledActionsContainer disabled={isAutoCompleteDisabled}>
          {liveReportInfo.status === 'loading' ||
          liveReportSuggestedUserList.status === 'loading' ? (
            <StyledLoadingField mode={theme}>
              <StyledLogo>
                <QLogo fontSize={16} mode={theme} />
              </StyledLogo>
              {t('loadingThreeDot')}
            </StyledLoadingField>
          ) : (
            <Autocomplete
              ref={subscribersAutocompleteRef}
              mode={theme}
              options={suggestedUserList || []}
              textFieldProps={{
                fullWidth: true,
                placeholder: t('subscriberAutoCompletePlaceholder'),
                disabled: isAutoCompleteDisabled
              }}
              menuProps={{
                maxHeight: '222px',
                placement: 'bottom-start'
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>, newValue: SubscriberUserInfo) => {
                handleSelect(newValue)
              }}
              onInputChange={() => {
                setSelectedUser(null)
              }}
              onEnterFreeValue={handleAddSubscriber}
              filterOptionsConfig={{
                sortAlphabetically: true,
                limit: 10
              }}
            />
          )}
          <Button mode={theme} color="secondary" onClick={handleAddSubscriber}>
            {t('add')}
          </Button>
        </StyledActionsContainer>
      </Tooltip>
      <StyledSubscribersListContainer
        $mode={theme}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...register('subscribersList' as const)}>
        {liveReportInfo.status === 'loading' || liveReportSuggestedUserList.status === 'loading' ? (
          <StyledLoadingList>
            <StyledLogo>
              <QLogo fontSize={16} mode={theme} />
            </StyledLogo>
          </StyledLoadingList>
        ) : (
          (fields as { id: string; name?: string; key: string }[]).map((item, index) => {
            let subscriber
            if (suggestedUserLookup.get(item.id)) {
              subscriber = suggestedUserLookup.get(item.id)
            } else {
              subscriber = ({
                userProfileId: item.id,
                userName: item.name
              } as unknown) as SuggestedUserInfo
            }

            return (
              subscriber && (
                <StyledSubscribersListItem mode={theme} key={item.key}>
                  <SubscriberEmailItem formattedSubscriber={formatSubscriberDisplay(subscriber)} />
                  {handleLiveReportSubscriber(subscriber!) && (
                    <StyledSubscribersHint mode={theme}>
                      {handleLiveReportSubscriber(subscriber!)}
                    </StyledSubscribersHint>
                  )}
                  <IconButton
                    type="button"
                    onClick={() => handleRemoveSubscriber(index)}
                    mode={theme}
                    color={sys[theme].interaction.onElevation.hover}
                    variant="clickable">
                    <Trash color={sys[theme].text.placeholder} />
                  </IconButton>
                </StyledSubscribersListItem>
              )
            )
          })
        )}
      </StyledSubscribersListContainer>
    </StyledSubscribersArea>
  )
}

export default Subscribers
